/**
 * Error and stall recovery (spec.md §13.6).
 *
 * A mobile network does not fail cleanly. What actually happens on a train or in a lift is
 * one of these, and they need different treatment:
 *
 * | Symptom | `el.error?.code` | Treatment |
 * |---|---|---|
 * | user hit skip mid-load | `1` `MEDIA_ERR_ABORTED` | **ignore** — this is a user action |
 * | connection dropped | `2` `MEDIA_ERR_NETWORK` | jittered backoff, soft reload |
 * | a range response was truncated | `3` `MEDIA_ERR_DECODE` | same as network — a short 206 surfaces as a *decode* failure, not a network one |
 * | the media token expired | `4` `MEDIA_ERR_SRC_NOT_SUPPORTED` | **never retry the same URL**; re-mint once, then skip the track |
 * | element errored with no code | `null` | treat as retryable network |
 *
 * That last row is not theoretical: **`el.error` can be `null` inside an `error`
 * handler** in some engines, so the code is read as `el.error?.code` and an absent code
 * gets the retryable branch rather than being dropped.
 *
 * ## The soft reload
 *
 * Each attempt saves `currentTime`, calls `load()`, restores `currentTime` and resumes if
 * it had been playing. The restore is written **while `readyState` is back at
 * `HAVE_NOTHING`**, so it becomes the element's default playback start position and the
 * track resumes exactly where it stopped, with no audible jump back to 0. `load()` is also
 * the only thing that clears a latched `MEDIA_ERR_SRC_NOT_SUPPORTED`, and after an error
 * with `networkState === NETWORK_NO_SOURCE` it is mandatory — the element will not try
 * again on its own.
 *
 * ## The watchdog, and why it is not `stalled`
 *
 * `stalled` fires **once**, roughly 3 s after data stops arriving, and never again. It is
 * therefore useless as a liveness signal on its own. `progress`, by contrast, fires every
 * ~350 ms (±200 ms) while a resource is being fetched, and `timeupdate` about 4×/second
 * while playing. So liveness is "did either of those happen recently", polled on a **3 s
 * interval** with a **12 s threshold**.
 *
 * `suspend` is the *normal* end of buffering — the element has everything it wants — and
 * must never be treated as a failure; it disarms the watchdog until real activity resumes.
 *
 * **No `requestAnimationFrame` anywhere in this file.** rAF stops on a hidden page, which
 * is precisely when a background set most needs the watchdog. `setInterval` keeps running:
 * Telegram deliberately leaves `webView.pauseTimers()` commented out.
 */

import { MEDIA_ERROR, audio, type MediaElementLike } from './element';

import { subscribeEngineEvents, type EngineEvent, type Unsubscribe } from './events';
import { safePlay } from './unlock';

/* ══ classification ════════════════════════════════════════════════════════════════ */

/**
 * How a media failure should be treated.
 *
 * `unknown` exists because an `error` event with no readable `MediaError` is a real
 * browser behaviour, not a bug in the caller.
 */
export type MediaFailure = 'aborted' | 'network' | 'decode' | 'unsupported' | 'unknown';

/** Maps `MediaError.code` (or its absence) onto a {@link MediaFailure}. */
export function classifyMediaError(code: number | null | undefined): MediaFailure {
  switch (code) {
    case MEDIA_ERROR.ABORTED:
      return 'aborted';
    case MEDIA_ERROR.NETWORK:
      return 'network';
    case MEDIA_ERROR.DECODE:
      return 'decode';
    case MEDIA_ERROR.SRC_NOT_SUPPORTED:
      return 'unsupported';
    default:
      return 'unknown';
  }
}

/** `true` for the failures that a soft reload of the same URL can plausibly fix. */
export function isRetryable(failure: MediaFailure): boolean {
  return failure === 'network' || failure === 'decode' || failure === 'unknown';
}

/* ══ backoff ═══════════════════════════════════════════════════════════════════════ */

/** The backoff ladder in milliseconds. Five rungs, then the track is given up on. */
export const BACKOFF_LADDER_MS = [400, 900, 2000, 4500, 9000] as const;

/**
 * Jitter as a fraction of the rung, applied symmetrically (±25%).
 *
 * Without it, every client that lost the same cell tower retries in the same 50 ms window
 * and the recovery itself becomes the outage.
 */
export const BACKOFF_JITTER = 0.25;

/**
 * The delay for a zero-based attempt index, jittered.
 *
 * Attempts past the last rung clamp to it, so a caller that ignores {@link isExhausted}
 * still gets a sane number rather than `NaN`.
 */
export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const index = Math.min(Math.max(Math.trunc(attempt), 0), BACKOFF_LADDER_MS.length - 1);
  const base = BACKOFF_LADDER_MS[index] ?? BACKOFF_LADDER_MS[BACKOFF_LADDER_MS.length - 1] ?? 9000;
  const spread = random() * 2 - 1; // [-1, 1)
  const jittered = base * (1 + BACKOFF_JITTER * spread);
  return Math.max(0, Math.round(jittered));
}

/** `true` once every rung of the ladder has been used. */
export function isExhausted(attempts: number): boolean {
  return attempts >= BACKOFF_LADDER_MS.length;
}

/* ══ watchdog constants ════════════════════════════════════════════════════════════ */

/** Poll period. Matches the ~3 s the HTML spec suggests for the stall timeout. */
export const WATCHDOG_INTERVAL_MS = 3_000;

/**
 * How long the element may go without `progress` **or** `timeupdate` while unpaused before
 * the stream is declared dead. Four poll periods: long enough that a slow 3G chunk is not
 * mistaken for a stall, short enough that a user does not give up first.
 */
export const WATCHDOG_THRESHOLD_MS = 12_000;

/* ══ controller ════════════════════════════════════════════════════════════════════ */

/** One recovery attempt, reported before its timer fires. */
export interface RecoveryAttempt {
  readonly failure: MediaFailure;
  /** 1-based rung of the ladder. */
  readonly attempt: number;
  readonly delayMs: number;
  /** The position that will be restored after `load()`. */
  readonly positionS: number;
  readonly wasPlaying: boolean;
  /** `true` when the attempt re-minted the media URL instead of reusing it. */
  readonly refreshedSrc: boolean;
}

export interface RecoveryOptions {
  readonly el?: MediaElementLike;
  /**
   * Re-mints the media URL after `MEDIA_ERR_SRC_NOT_SUPPORTED` (usually an expired media
   * token). Called **at most once per track**. Returning `null` — or throwing — means the
   * track is unplayable and gets skipped.
   */
  readonly refreshSrc?: () => Promise<string | null> | string | null;
  /** Called for every scheduled attempt, before its delay elapses. */
  readonly onAttempt?: (attempt: RecoveryAttempt) => void;
  /** Called when the watchdog declares the stream dead. */
  readonly onStall?: (info: { readonly sinceMs: number }) => void;
  /** Called when the ladder is exhausted or the URL could not be re-minted. */
  readonly onGiveUp?: (info: { readonly failure: MediaFailure; readonly attempts: number }) => void;
  /** Jitter source, for tests. */
  readonly random?: () => number;
  /** Clock, for tests. Must agree with whatever drives `setInterval`. */
  readonly now?: () => number;
  readonly intervalMs?: number;
  readonly thresholdMs?: number;
}

export interface RecoveryController {
  /** Subscribes to the element and starts the watchdog. Idempotent. */
  start(): void;
  /** Unsubscribes, cancels any pending attempt, stops the watchdog. Idempotent. */
  stop(): void;
  /** Clears the ladder and the one-shot re-mint. Call on **track change**. */
  reset(): void;
  /** Rungs of the ladder consumed so far. */
  readonly attempts: number;
  /** Drives a failure by hand — used by the wiring for a failed fetch of the URL itself. */
  handleFailure(failure: MediaFailure): void;
}

function positionOf(el: MediaElementLike): number {
  const t = el.currentTime;
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/**
 * Creates the recovery controller for one element.
 *
 * The controller owns its subscription and its interval; nothing about it is global, so a
 * test can create one per case and `stop()` it in teardown.
 */
export function createRecovery(options: RecoveryOptions = {}): RecoveryController {
  const el = options.el ?? audio;
  const random = options.random ?? Math.random;
  const now = options.now ?? Date.now;
  const intervalMs = options.intervalMs ?? WATCHDOG_INTERVAL_MS;
  const thresholdMs = options.thresholdMs ?? WATCHDOG_THRESHOLD_MS;

  let attempts = 0;
  let refreshUsed = false;
  let pending: ReturnType<typeof setTimeout> | null = null;
  let poll: ReturnType<typeof setInterval> | null = null;
  let unsubscribe: Unsubscribe | null = null;
  let lastActivityAt = now();
  /**
   * The watchdog only fires while armed. It is disarmed by `suspend` (normal end of
   * buffering), by `pause`/`ended`, and by its own firing — so one stall episode produces
   * exactly one recovery, and the next episode needs fresh evidence of activity first.
   */
  let armed = true;

  function cancelPending(): void {
    if (pending === null) return;
    clearTimeout(pending);
    pending = null;
  }

  /**
   * `load()` → restore position → resume. The position is written while the element is
   * back at `HAVE_NOTHING`, which makes it the default playback start position rather
   * than a seek. See the module header.
   */
  function softReload(positionS: number, wasPlaying: boolean): void {
    try {
      el.load();
    } catch {
      // A throwing `load()` leaves nothing to restore into; the next failure re-enters
      // here with the next rung of the ladder.
      return;
    }
    if (positionS > 0) {
      try {
        el.currentTime = positionS;
      } catch {
        // Some engines throw on a write before the load algorithm has settled. Losing
        // the resume position is strictly better than losing playback.
      }
    }
    if (wasPlaying) void safePlay(undefined, el);
  }

  function scheduleReload(failure: MediaFailure): void {
    if (isExhausted(attempts)) {
      options.onGiveUp?.({ failure, attempts });
      return;
    }
    const delayMs = backoffDelayMs(attempts, random);
    attempts += 1;
    const positionS = positionOf(el);
    const wasPlaying = !el.paused;

    cancelPending();
    pending = setTimeout(() => {
      pending = null;
      lastActivityAt = now();
      softReload(positionS, wasPlaying);
    }, delayMs);

    options.onAttempt?.({
      failure,
      attempt: attempts,
      delayMs,
      positionS,
      wasPlaying,
      refreshedSrc: false,
    });
  }

  /**
   * `MEDIA_ERR_SRC_NOT_SUPPORTED`: the URL itself is the problem, so retrying it is
   * pointless. The one plausible benign cause is an expired media token, so the URL is
   * re-minted **once** and, if that fails, the track is given up on.
   */
  function refreshAndReload(): void {
    const refreshSrc = options.refreshSrc;
    if (refreshUsed || refreshSrc === undefined) {
      options.onGiveUp?.({ failure: 'unsupported', attempts });
      return;
    }
    refreshUsed = true;
    const positionS = positionOf(el);
    const wasPlaying = !el.paused;

    options.onAttempt?.({
      failure: 'unsupported',
      attempt: attempts + 1,
      delayMs: 0,
      positionS,
      wasPlaying,
      refreshedSrc: true,
    });

    const run = async (): Promise<void> => {
      let next: string | null = null;
      try {
        next = await refreshSrc();
      } catch {
        next = null;
      }
      if (next === null || next === '') {
        options.onGiveUp?.({ failure: 'unsupported', attempts });
        return;
      }
      el.src = next;
      lastActivityAt = now();
      softReload(positionS, wasPlaying);
    };
    void run();
  }

  function handleFailure(failure: MediaFailure): void {
    if (failure === 'aborted') return; // user action; nothing to recover from
    armed = false;
    if (failure === 'unsupported') {
      refreshAndReload();
      return;
    }
    scheduleReload(failure);
  }

  function onEvent(event: EngineEvent): void {
    switch (event.kind) {
      case 'error':
        handleFailure(classifyMediaError(event.snapshot.errorCode));
        return;

      case 'suspend':
        // The NORMAL end of buffering. Never an error.
        armed = false;
        return;

      case 'pause':
      case 'ended':
        armed = false;
        return;

      case 'progress':
      case 'time':
      case 'waiting':
      case 'stalled':
      case 'playing':
      case 'seeked':
        lastActivityAt = event.at;
        armed = true;
        return;

      case 'metadata':
      case 'duration':
      case 'ratechange':
        lastActivityAt = event.at;
        return;

      default:
        return;
    }
  }

  function tick(): void {
    const at = now();
    // While paused the clock is kept fresh, so un-pausing never starts life 12 s stale.
    if (el.paused) {
      lastActivityAt = at;
      return;
    }
    if (!armed) return;
    const sinceMs = at - lastActivityAt;
    if (sinceMs < thresholdMs) return;
    armed = false;
    options.onStall?.({ sinceMs });
    scheduleReload('network');
  }

  return {
    start(): void {
      if (unsubscribe !== null) return;
      lastActivityAt = now();
      armed = true;
      // tickMs: 0 — the watchdog must not be fed by its own heartbeat, only by the
      // element's real `progress`/`timeupdate`.
      unsubscribe = subscribeEngineEvents(onEvent, { el, tickMs: 0, now });
      poll = setInterval(tick, intervalMs);
    },

    stop(): void {
      cancelPending();
      if (poll !== null) {
        clearInterval(poll);
        poll = null;
      }
      if (unsubscribe !== null) {
        unsubscribe();
        unsubscribe = null;
      }
    },

    reset(): void {
      cancelPending();
      attempts = 0;
      refreshUsed = false;
      lastActivityAt = now();
      armed = true;
    },

    get attempts(): number {
      return attempts;
    },

    handleFailure,
  };
}
